"use client";

import React, { useCallback, ChangeEvent, useEffect } from "react";

import { useMediaQuery } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import clsx from "clsx";

import { BffComponentType } from "~/bff/ComponentsConfig";
import { SearchPanel } from "~/bff/types/SearchPanel";
import { SearchField } from "~/components/search-field/component";
import { UniversalComponent } from "~/components/universal-component/component";
import { BffComponentExtended } from "~/components/universal-component/types";
import { searchPanelConfig } from "~/constants/components-configs/search-panel-config";
import { HeaderContext } from "~/context/header/context";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Close24 } from "~/theme/icons/components/close";
import { Search24 } from "~/theme/icons/components/search";
import { getFormatMedia } from "~/theme/utils";
import { Nullable } from "~/types/general.types";

import { SEARCH_PANEL_MOBILE_CLASSES } from "./constants";
import { SearchPanelMobileContainer } from "./styled";

export interface SearchPanelMobileProps {
  searchPanel?: Nullable<SearchPanel>;
  className?: string;
}

const SEARCH_BUTTON_ID = "search-button";

export const SearchPanelMobile: React.FC<SearchPanelMobileProps> = ({
  searchPanel,
  className,
}) => {
  const theme = useTheme();
  const {
    query,
    isSearchOpen,
    handleQueryChange,
    handleQueryClear,
    handleSearchClick,
    handleSearchClose,
  } = React.useContext(HeaderContext) ?? {};
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (handleQueryChange) {
        handleQueryChange(event.target.value);
      }
    },
    [handleQueryChange],
  );

  const handleSearch = useCallback(() => {
    if (handleSearchClick) {
      handleSearchClick();
    }
    if (handleSearchClose) {
      handleSearchClose();
    }
  }, [handleSearchClick, handleSearchClose]);

  const onCloseButtonClick = useCallback(() => {
    handleSearchClose?.();
    handleQueryClear?.();
  }, [handleSearchClose, handleQueryClear]);

  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isSearchOpen]);

  const { BREAKPOINT_DESKTOP } = getFormatMedia(theme);
  const isDesktopView = useMediaQuery(BREAKPOINT_DESKTOP);

  if (isDesktopView) {
    return;
  }

  return (
    <SearchPanelMobileContainer
      PaperProps={{
        className: clsx(SEARCH_PANEL_MOBILE_CLASSES.drawer, className),
        ...getTestAutomationProps("search-panel"),
      }}
      anchor="right"
      open={isSearchOpen}
      onClose={handleSearchClose}
    >
      <Toolbar className={SEARCH_PANEL_MOBILE_CLASSES.toolbar} role="search">
        <IconButton
          color="inherit"
          aria-label="close search panel"
          onClick={onCloseButtonClick}
          {...getTestAutomationProps("close-button")}
          size="large"
        >
          <Close24 />
        </IconButton>
        <SearchField
          withIcon={false}
          value={query}
          onChange={handleChange}
          onClear={handleQueryClear}
          onSearch={handleSearch}
          onEscape={handleSearchClose}
          translations={searchPanel?.props?.translations}
        />
        <IconButton
          color="inherit"
          aria-label={
            searchPanel?.props?.translations?.searchText || SEARCH_BUTTON_ID
          }
          onClick={handleSearch}
          {...getTestAutomationProps(SEARCH_BUTTON_ID)}
          size="large"
        >
          <Search24 />
        </IconButton>
      </Toolbar>
      {searchPanel && (
        <UniversalComponent
          meta={searchPanel._meta}
          props={searchPanel.props}
          component={searchPanel.component ? BffComponentType.SEARCH_PANEL : null}
          elements={searchPanel.children as BffComponentExtended[]}
          componentsConfigs={[searchPanelConfig]}
        />
      )}
    </SearchPanelMobileContainer>
  );
};
